import React, { useMemo } from "react"
import { useQuery, useMutation } from "@apollo/client/react"
import { Formik } from "formik"
import { toast } from "sonner"
import { useModalAnalytics } from "@love-irish/analytics"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@love-irish/ui-components/dialog"
import Button from "@love-irish/core-button"
import { Label } from "@love-irish/ui-components/label"

import AvatarGrid from "./avatar_grid"
import BannerGrid from "./banner_grid"
import PreviewPanel from "./preview_panel"

import query from "./get_profile_customization_options.graphql"
import mutation from "./update_profile_appearance.graphql"
import CommunityLofiProfileComponent from "@love-irish/community-lofi-profile-component"
import routes from "@love-irish/routes"

// Identifies this modal in the generic modal.opened / modal.submitted analytics events.
const MODAL_NAME = "profile_customize"

interface ProfileEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProfileEditModal({
  open,
  onOpenChange,
}: ProfileEditModalProps) {
  const { data, loading: queryLoading } = useQuery(query, {
    skip: !open,
    fetchPolicy: "cache-and-network",
  })

  const [updateProfile, { loading: mutationLoading }] = useMutation(mutation)

  // Fires modal.opened on open; trackSubmitted() fires modal.submitted.
  const { trackSubmitted } = useModalAnalytics(MODAL_NAME, open)

  const profile = data?.currentUser?.profile
  const availableIcons = data?.currentUser?.availableProfileIcons?.nodes || []
  const availableBanners =
    data?.currentUser?.availableProfileBanners?.nodes || []

  const initialValues = useMemo(
    () => ({
      avatarId: profile?.avatar?.id || null,
      bannerId: profile?.banner?.id || null,
    }),
    [profile?.avatar?.id, profile?.banner?.id]
  )

  // Find the URLs for the selected items
  const getAvatarUrl = (id: string | null) => {
    if (!id) return null
    const avatar = availableIcons.find((a) => a.id === id)
    return avatar?.imageUrl || null
  }

  const getBannerUrl = (id: string | null) => {
    if (!id) return null
    const banner = availableBanners.find((b) => b.id === id)
    return banner?.imageUrl || null
  }

  const isLoading = queryLoading || mutationLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customize Your Profile</DialogTitle>
          <DialogDescription>
            Choose an avatar and banner to personalize your profile.{" "}
            <a
              href={routes.settingsProfilePath()}
              className="text-primary-600 hover:underline"
            >
              Edit more settings
            </a>
          </DialogDescription>
        </DialogHeader>

        {queryLoading ? (
          <div className="py-8 text-center text-gray-500">
            Loading your customization options...
          </div>
        ) : !profile ? (
          <div className="py-8 text-center text-gray-500">
            Please sign in to customize your profile.
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const result = await updateProfile({
                  variables: {
                    input: {
                      name: profile.name,
                      profileIconId: values.avatarId,
                      bannerId: values.bannerId,
                    },
                  },
                })

                if (result.data?.updateUserProfile?.success) {
                  trackSubmitted()
                  toast.success("Profile updated successfully!", {
                    action: {
                      label: "View profile",
                      onClick: () =>
                        (window.location.href = routes.profilePath(
                          profile.slug
                        )),
                    },
                  })
                  onOpenChange(false)
                } else if (result.data?.updateUserProfile?.errors?.length > 0) {
                  const errorMessages = result.data.updateUserProfile.errors
                    .map((err) => err.message)
                    .join(", ")
                  toast.error(errorMessages)
                } else {
                  toast.error("There was a problem updating your profile")
                }
              } catch (err) {
                console.error("Failed to update profile:", err)
                toast.error("Failed to update profile. Please try again later.")
              } finally {
                setSubmitting(false)
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit, isSubmitting, dirty }) => (
              <form onSubmit={handleSubmit}>
                <div className="grid gap-6 pb-8 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <CommunityLofiProfileComponent
                      banner={getBannerUrl(values.bannerId)}
                      avatar={getAvatarUrl(values.avatarId)}
                    />
                  </div>

                  <div className="space-y-6">
                    {/* Avatar Selection */}
                    <div>
                      <Label className="mb-3 block">Avatar</Label>
                      <AvatarGrid
                        avatars={availableIcons}
                        selectedId={values.avatarId}
                        onSelect={(id) => setFieldValue("avatarId", id)}
                        disabled={isLoading || isSubmitting}
                      />
                    </div>

                    {/* Banner Selection */}
                    <div>
                      <Label className="mb-3 block">Banner</Label>
                      <BannerGrid
                        banners={availableBanners}
                        selectedId={values.bannerId}
                        onSelect={(id) => setFieldValue("bannerId", id)}
                        disabled={isLoading || isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isLoading || isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || isSubmitting || !dirty}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ProfileEditModal
